import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { subscriptionAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, Sparkles, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Simple layout for unauthenticated users
function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-xl font-serif text-foreground">
              ClarityPath <span className="text-muted-foreground">—</span> <span className="text-accent">Guided by Mo</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-sm font-medium hover:text-primary duration-300">Home</Link>
              <Button variant="outline" size="sm" asChild className="rounded-full">
                <Link to="/login">Log In</Link>
              </Button>
              <Button size="sm" asChild className="rounded-full bg-primary hover:bg-primary/90">
                <Link to="/signup">Get Started</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-6 py-8">
        {children}
      </main>
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPackage, setProcessingPackage] = useState(null);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await subscriptionAPI.getPackages();
      setPackages(response.data.packages);
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    if (packageId === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    setProcessingPackage(packageId);

    try {
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.createCheckout(packageId, originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPackage(null);
    }
  };

  const getButtonText = (pkg) => {
    if (pkg.is_application) return 'Apply for Private Coaching';
    if (processingPackage === pkg.id) return 'Processing...';
    if (user?.subscription_tier === pkg.id) return 'Current Plan';
    if (pkg.id === 'open_path') return user ? 'Current Plan' : 'Start Free';
    return user ? 'Upgrade' : 'Get Started';
  };

  // Choose layout based on auth status
  const Layout = user ? DashboardLayout : PublicLayout;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading pricing...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-10" data-testid="pricing-page">
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-serif mb-4">Choose Your Path to Clarity</h1>
          <p className="text-muted-foreground text-lg">
            Start where you are — and choose the level of guidance that fits your journey.
          </p>
        </div>

        {/* Main Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {packages.map((pkg, index) => {
            const isCurrent = user?.subscription_tier === pkg.id;
            const isPopular = pkg.badge === 'Popular';
            const isPremium = pkg.badge === 'Premium';
            
            return (
              <Card
                key={pkg.id}
                className={`p-6 rounded-2xl relative flex flex-col ${
                  isPopular ? 'border-2 border-accent shadow-lg' :
                  isPremium ? 'border-2 border-primary shadow-lg' :
                  'border-border'
                }`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className={`px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                      isPremium ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'
                    }`}>
                      <Sparkles size={12} />
                      {pkg.badge}
                    </div>
                  </div>
                )}

                {isCurrent && (
                  <div className="absolute -top-3 right-3">
                    <div className="bg-foreground text-background px-3 py-1 rounded-full text-xs font-semibold">
                      Active
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-serif mb-1">{pkg.name}</h3>
                  {pkg.tagline && (
                    <p className="text-sm text-muted-foreground mb-3">{pkg.tagline}</p>
                  )}
                  {pkg.is_application ? (
                    <div className="py-2">
                      <span className="text-lg font-serif text-foreground/80">By Application</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-serif text-foreground">${pkg.price}</span>
                      <span className="text-muted-foreground text-sm">
                        /{pkg.billing === 'forever' ? 'forever' : 'mo'}
                      </span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={`${pkg.id}-feature-${fIndex}`} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${
                        isPremium ? 'text-primary' : 'text-accent'
                      }`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => pkg.is_application ? navigate('/apply/coaching') : handleUpgrade(pkg.id)}
                  disabled={!pkg.is_application && (processingPackage !== null || isCurrent)}
                  data-testid={`upgrade-btn-${pkg.id}`}
                  variant={pkg.id === 'free' ? 'outline' : 'default'}
                  className={`w-full rounded-full ${
                    isPopular ? 'bg-accent hover:bg-accent/90' :
                    isPremium ? 'bg-primary hover:bg-primary/90' :
                    ''
                  }`}
                >
                  {getButtonText(pkg)}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Feature Comparison */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-serif text-center mb-8">Compare Plans</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium">Feature</th>
                  <th className="text-center py-3 px-4 font-medium">Open</th>
                  <th className="text-center py-3 px-4 font-medium text-accent">Supported</th>
                  <th className="text-center py-3 px-4 font-medium">Guided</th>
                  <th className="text-center py-3 px-4 font-medium text-primary">Personal</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Messages with Clair</td>
                  <td className="text-center py-3 px-4">20/month</td>
                  <td className="text-center py-3 px-4 text-accent font-medium">Unlimited</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Goals & Action Plans</td>
                  <td className="text-center py-3 px-4">1 goal</td>
                  <td className="text-center py-3 px-4 text-accent font-medium">Unlimited</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Journal with Emotion Wheel</td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-primary" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Insight Tracker (Clair's Memory)</td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-primary" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Monthly Reflection</td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-primary" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Content Library</td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-primary" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">Small Groups</td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-accent" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-primary" /></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-4">1-on-1 Coaching with Mo</td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><X size={16} className="inline text-muted-foreground" /></td>
                  <td className="text-center py-3 px-4"><Check size={16} className="inline text-primary" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-center text-sm text-muted-foreground max-w-2xl mx-auto">
          <p>
            All plans include secure payment via Stripe. Cancel anytime.
            Your free messages reset at the start of each month.
          </p>
        </div>
      </div>
    </Layout>
  );
}
